/**
 * 发起讨论弹窗组件
 */
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCommunityCategories } from "@/hooks/useCommunityCategories";
import { MessageSquare, Tag } from "lucide-react";
import HeadlessDialog from './Dialog/HeadlessDialog';
import { useToast } from '@/hooks/use-toast';
import { communityApi, CreateTopicParams } from '@/api/modules/community';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Placeholder from '@tiptap/extension-placeholder';
import { Mark, mergeAttributes } from '@tiptap/core';

// 图片上传状态
interface ImageUploadState {
  isUploading: boolean;
  progress: number;
  error: string | null;
}

// 自定义字体大小扩展
const FontSize = Mark.create({
  name: 'fontSize',
  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },
  addAttributes() {
    return {
      size: {
        default: null,
        parseHTML: element => element.style.fontSize,
        renderHTML: attributes => {
          if (!attributes.size) {
            return {};
          }
          return {
            style: `font-size: ${attributes.size};`,
          };
        },
      },
    };
  },
  parseHTML() {
    return [
      {
        style: 'font-size',
      },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
  },
  addCommands() {
    return {
      setFontSize: size => ({ chain }) => {
        return chain().setMark('fontSize', { size }).run();
      },
      unsetFontSize: () => ({ chain }) => {
        return chain().unsetMark('fontSize').run();
      },
    };
  },
});

interface CreateTopicDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  // 编辑模式相关
  isEditMode?: boolean;
  topicData?: {
    id?: string | number;
    title?: string;
    topicTypeId?: string | number;
    content?: string;
    tags?: string;
  };
}

const CreateTopicDialog = ({ isOpen, onClose, onSuccess, isEditMode = false, topicData }: CreateTopicDialogProps) => {
  const [formData, setFormData] = useState({
    title: "",
    category: "",
    content: "",
    tags: ""
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imageUpload, setImageUpload] = useState<ImageUploadState>({
    isUploading: false,
    progress: 0,
    error: null
  });
  const { toast } = useToast();
  
  // 使用社区分类 Hook
  const { categories } = useCommunityCategories();

  // 图片上传处理
  const uploadImage = async (file: File): Promise<string> => {
    if (file.size > 5 * 1024 * 1024) {
      throw new Error('图片大小不能超过 5MB');
    }

    setImageUpload({ isUploading: true, progress: 0, error: null });

    try {
      // TODO: 实现真实的图片上传逻辑
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // 模拟上传成功后的 URL（实际应该从服务器返回）
      const imageUrl = URL.createObjectURL(file);
      
      setImageUpload({ isUploading: false, progress: 100, error: null });
      return imageUrl;
    } catch (error) {
      setImageUpload({ isUploading: false, progress: 0, error: '上传失败' });
      throw error;
    }
  };

  // Tiptap 编辑器配置
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        underline: false,
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Color,
      Highlight,
      FontSize,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'rounded-lg max-w-full',
        },
      }),
      Placeholder.configure({
        placeholder: '请详细描述您的问题或想法...支持富文本格式，包括文字、图片、列表等',
      }),
    ],
    content: formData.content,
    onUpdate: ({ editor }) => {
      handleInputChange('content', editor.getHTML());
    },
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-xl focus:outline-none min-h-[400px] p-4 max-w-none',
      },
    },
  });

  // 重置表单
  const resetForm = () => {
    setFormData({
      title: "",
      category: "",
      content: "",
      tags: ""
    });
    setErrors({});
    setIsSubmitting(false);
  };

  // 当对话框关闭时重置表单
  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  // 编辑模式下，加载话题数据
  useEffect(() => {
    if (isOpen && isEditMode && topicData) {
      setFormData({
        title: topicData.title || "",
        category: String(topicData.topicTypeId || ""),
        content: topicData.content || "",
        tags: topicData.tags || ""
      });
      
      // 如果有编辑器实例，更新内容
      if (editor && topicData.content) {
        editor.commands.setContent(topicData.content);
      }
    }
  }, [isOpen, isEditMode, topicData, editor]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ""
      }));
    }
  };

  // 处理编辑器内容变化
  const handleEditorChange = (content: string) => {
    handleInputChange('content', content);
  };

  // 图片上传处理
  const handleImageUpload = async () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      try {
        const imageUrl = await uploadImage(file);
        if (editor) {
          editor.chain().focus().setImage({ src: imageUrl }).run();
        }
        toast({
          title: "上传成功",
          description: "图片已成功上传",
        });
      } catch (error) {
        console.error('图片上传失败:', error);
        toast({
          title: "上传失败",
          description: error instanceof Error ? error.message : "请稍后重试",
          variant: "destructive",
        });
      }
    };
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = "请输入讨论标题";
    } else if (formData.title.length < 5) {
      newErrors.title = "标题至少需要5个字符";
    } else if (formData.title.length > 100) {
      newErrors.title = "标题不能超过100个字符";
    }

    if (!formData.category) {
      newErrors.category = "请选择讨论分类";
    }

    if (!formData.content.trim()) {
      newErrors.content = "请输入讨论内容";
    } else if (formData.content.length < 10) {
      newErrors.content = "内容至少需要10个字符";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    // 验证表单
    if (!validateForm()) {
      return;
    }

    // 检查是否登录
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!token) {
      toast({
        title: "未登录",
        description: "请先登录后再发起讨论",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const params: CreateTopicParams = {
        title: formData.title,
        topicTypeId: formData.category,
        content: formData.content,
        tags: formData.tags,
      };

      // 编辑模式下添加 id
      if (isEditMode && topicData?.id) {
        params.id = topicData.id;
      }
      
      // 根据模式调用不同的 API
      const response = isEditMode 
        ? await communityApi.updateTopic(params)
        : await communityApi.createTopic(params);

      if (response?.code === 200) {
        toast({
          title: isEditMode ? "编辑成功" : "发布成功",
          description: isEditMode ? "话题已更新" : "您的讨论已成功发布",
        });

        resetForm();
        onSuccess();
        onClose();
      } else {
        toast({
          title: isEditMode ? "编辑失败" : "发布失败",
          description: response?.msg || `${isEditMode ? '编辑' : '发布'}讨论失败，请稍后重试`,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error(`${isEditMode ? '编辑' : '发布'}讨论失败:`, error);
      toast({
        title: isEditMode ? "编辑失败" : "发布失败",
        description: `${isEditMode ? '编辑' : '发布'}讨论失败，请稍后重试`,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      resetForm();
      onClose();
    }
  };

  return (
    <HeadlessDialog
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditMode ? "编辑讨论" : "发起讨论"}
      description={isEditMode ? "修改您的讨论内容" : "分享您的想法和经验，与社区成员交流"}
      size="xl2"
      isShowClose={false}
    >
      <div className="space-y-6 py-4">
        {/* 标题 */}
        <div>
          <Label htmlFor="topic-title" className="flex items-center space-x-2 mb-2">
            <MessageSquare className="w-4 h-4"/>
            <span>讨论标题 *</span>
          </Label>
          <Input
            id="topic-title"
            value={formData.title}
            onChange={(e) => handleInputChange("title", e.target.value)}
            placeholder="请输入一个清晰的标题，让大家更容易理解您的问题"
            className={errors.title ? "border-red-500" : ""}
            maxLength={100}
            disabled={isSubmitting}
          />
          {errors.title && (
            <p className="text-sm text-red-500 mt-1">{errors.title}</p>
          )}
          <p className="text-xs text-gray-500 mt-1">
            {formData.title.length}/100
          </p>
        </div>

        {/* 分类 */}
        <div>
          <Label htmlFor="topic-category" className="flex items-center space-x-2 mb-2">
            <Tag className="w-4 h-4"/>
            <span>讨论分类 *</span>
          </Label>
          <Select 
            value={formData.category} 
            onValueChange={(value) => handleInputChange("category", value)}
            disabled={isSubmitting}
          >
            <SelectTrigger className={errors.category ? "border-red-500" : ""}>
              <SelectValue placeholder="请选择讨论所属的分类" />
            </SelectTrigger>
            <SelectContent>
              {categories
                .filter(cat => cat.dictValue !== "-1") // 过滤掉"全部讨论"
                .map((category) => {
                  const IconComponent = category.icon;
                  return (
                    <SelectItem key={category.dictValue} value={category.dictValue}>
                      <div className="flex items-center space-x-2">
                        <IconComponent className="w-4 h-4" />
                        <span>{category.dictLabel}</span>
                      </div>
                    </SelectItem>
                  );
                })
              }
            </SelectContent>
          </Select>
          {errors.category && (
            <p className="text-sm text-red-500 mt-1">{errors.category}</p>
          )}
        </div>

        {/* 内容 - 富文本编辑器 */}
        <div>
          <Label htmlFor="topic-content" className="flex items-center space-x-2 mb-2">
            <MessageSquare className="w-4 h-4"/>
            <span>讨论内容 *</span>
          </Label>
          
          {/* Tiptap 工具栏 */}
          <div className="border border-gray-200 rounded-t-md bg-gray-50 p-2 flex flex-wrap gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => editor?.chain().focus().toggleBold().run()}
              disabled={isSubmitting || !editor}
              className={editor?.isActive('bold') ? 'bg-blue-100 text-blue-600' : ''}
              title="粗体"
            >
              <span className="font-bold">B</span>
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => editor?.chain().focus().toggleItalic().run()}
              disabled={isSubmitting || !editor}
              className={editor?.isActive('italic') ? 'bg-blue-100 text-blue-600' : ''}
              title="斜体"
            >
              <span className="italic">I</span>
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => editor?.chain().focus().toggleUnderline().run()}
              disabled={isSubmitting || !editor}
              className={editor?.isActive('underline') ? 'bg-blue-100 text-blue-600' : ''}
              title="下划线"
            >
              <span className="underline">U</span>
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => editor?.chain().focus().toggleStrike().run()}
              disabled={isSubmitting || !editor}
              className={editor?.isActive('strike') ? 'bg-blue-100 text-blue-600' : ''}
              title="删除线"
            >
              <span className="line-through">S</span>
            </Button>
            <div className="w-px h-6 bg-gray-300 mx-1" />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                const isActive = editor?.isActive('fontSize', { size: '2rem' });
                if (isActive) {
                  editor?.chain().focus().unsetFontSize().run();
                } else {
                  editor?.chain().focus().setFontSize('2rem').run();
                }
              }}
              disabled={isSubmitting || !editor}
              className={editor?.isActive('fontSize', { size: '2rem' }) ? 'bg-blue-100 text-blue-600' : ''}
              title="大标题"
            >
              H1
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                const isActive = editor?.isActive('fontSize', { size: '1.5rem' });
                if (isActive) {
                  editor?.chain().focus().unsetFontSize().run();
                } else {
                  editor?.chain().focus().setFontSize('1.5rem').run();
                }
              }}
              disabled={isSubmitting || !editor}
              className={editor?.isActive('fontSize', { size: '1.5rem' }) ? 'bg-blue-100 text-blue-600' : ''}
              title="中标题"
            >
              H2
            </Button>
            <div className="w-px h-6 bg-gray-300 mx-1" />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => editor?.chain().focus().setTextAlign('left').run()}
              disabled={isSubmitting || !editor}
              className={editor?.isActive({ textAlign: 'left' }) ? 'bg-blue-100 text-blue-600' : ''}
              title="左对齐"
            >
              ←
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => editor?.chain().focus().setTextAlign('center').run()}
              disabled={isSubmitting || !editor}
              className={editor?.isActive({ textAlign: 'center' }) ? 'bg-blue-100 text-blue-600' : ''}
              title="居中"
            >
              ↔
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => editor?.chain().focus().setTextAlign('right').run()}
              disabled={isSubmitting || !editor}
              className={editor?.isActive({ textAlign: 'right' }) ? 'bg-blue-100 text-blue-600' : ''}
              title="右对齐"
            >
              →
            </Button>
            <div className="w-px h-6 bg-gray-300 mx-1" />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleImageUpload}
              disabled={isSubmitting || !editor || imageUpload.isUploading}
              title="插入图片"
            >
              📷 图片
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                const url = prompt('请输入链接地址');
                if (url) {
                  editor?.chain().focus().setLink({ href: url }).run();
                }
              }}
              disabled={isSubmitting || !editor}
              className={editor?.isActive('link') ? 'bg-blue-100 text-blue-600' : ''}
              title="插入链接"
            >
              🔗 链接
            </Button>
            <div className="flex-1" />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => editor?.chain().focus().clearNodes().unsetAllMarks().run()}
              disabled={isSubmitting || !editor}
              title="清除格式"
            >
              清除
            </Button>
          </div>
          
          {/* 编辑器内容区域 */}
          <div className="border border-t-0 border-gray-200 rounded-b-md bg-white">
            <EditorContent editor={editor} className="min-h-[400px] max-h-[600px] overflow-y-auto p-4" />
          </div>
          
          {imageUpload.isUploading && (
            <div className="mt-2 text-sm text-blue-600 flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
              <span>图片上传中... {Math.round(imageUpload.progress)}%</span>
            </div>
          )}
          
          {errors.content && (
            <p className="text-sm text-red-500 mt-1">{errors.content}</p>
          )}
          <p className="text-xs text-gray-500 mt-1">
            {new DOMParser().parseFromString(formData.content, 'text/html').body.textContent?.length || 0} 字符
          </p>
        </div>

        {/* 标签 */}
        <div>
          <Label htmlFor="topic-tags" className="flex items-center space-x-2 mb-2">
            <Tag className="w-4 h-4"/>
            <span>标签（可选）</span>
          </Label>
          <Input
            id="topic-tags"
            value={formData.tags}
            onChange={(e) => handleInputChange("tags", e.target.value)}
            placeholder="输入标签，用逗号分隔，如：光学设计,优化,问题"
            disabled={isSubmitting}
          />
          <p className="text-xs text-gray-500 mt-1">
            标签可以帮助其他用户更容易找到您的讨论
          </p>
        </div>

        {/* 提示信息 */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-blue-900 mb-2">发帖须知</h4>
          <ul className="text-xs text-blue-700 space-y-1">
            <li>• 请遵守社区规范，文明交流</li>
            <li>• 标题要清晰明确，方便他人理解</li>
            <li>• 详细描述您的问题或想法，包括必要的背景信息</li>
            <li>• 选择合适的分类，方便其他用户找到相关讨论</li>
          </ul>
        </div>

        {/* 按钮 */}
        <div className="flex space-x-3 pt-4 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
            className="flex-1"
          >
            取消
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || !formData.title || !formData.category || !formData.content}
            className="flex-1"
          >
            {isSubmitting ? (isEditMode ? "更新中..." : "发布中...") : (isEditMode ? "保存修改" : "发布讨论")}
          </Button>
        </div>
      </div>
    </HeadlessDialog>
  );
};

export default CreateTopicDialog;
